import { ITokenManager, SSOTokens } from '../token-manager/token-manager.types';
import { V2Router } from '../router/v2.router';
import { PkceManager } from '../oidc/pkce-manager';
import { StateVerifier } from '../oidc/state-verifier';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { ClientConfig } from '../types';
import { validateScope } from '../lib/scope';
import { addPostMessageListener, createIframe } from './authn.util';
import { exchangeCodeForTokens } from '../oauth/oauth';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { Session } from './session';
import { TransactionManager } from '../transaction/transaction-manager';

type AuthnOptions = {
  router: V2Router;
  pkceManager: PkceManager;
  stateVerifier: StateVerifier;
  clientConfig: ClientConfig;
  tokenManager: ITokenManager;
};

export type GetTokensWithoutPromptOptions = {
  sessionToken: string;
  scopes?: Array<string>;
  state?: string;
};

/**
 * @deprecated - Will not work without 3rd party cookies. Use PKCE Redirect Flow instead
 */
export class Authn {
  private readonly router: V2Router;
  private readonly pkceManager: PkceManager;
  private readonly stateVerifier: StateVerifier;
  private readonly clientConfig: ClientConfig;
  private readonly tokenManager: ITokenManager;
  private readonly logger: Logger;
  private readonly transactionManager: TransactionManager;
  public readonly session: Session;

  constructor(args: AuthnOptions) {
    this.router = args.router;
    this.pkceManager = args.pkceManager;
    this.stateVerifier = args.stateVerifier;
    this.clientConfig = args.clientConfig;
    this.tokenManager = args.tokenManager;
    this.session = new Session(this.router);
    this.transactionManager = TransactionManager.getSingleton();
    this.logger = Logger.createLogger(LoggerNames.AUTHN);
  }

  /**
   * @deprecated - Will not work without 3rd party cookies. Use PKCE Redirect Flow instead
   * @param scopes
   * @param state
   * @param sessionToken
   */
  public async getTokensWithoutPrompt({
    scopes,
    state,
    sessionToken,
  }: GetTokensWithoutPromptOptions): Promise<SSOTokens> {
    this.logger.debug('Initializing authn flow');
    return await this.transactionManager.create('login', async () => {
      try {
        const { codeChallenge } = await this.pkceManager.createCodePair();
        const authState = this.stateVerifier.generate(state);
        const url = await this.router.login({
          prompt: 'none',
          client_id: this.clientConfig.clientId,
          redirect_uri: this.clientConfig.redirectUri,
          scope: validateScope(scopes?.join(' ') ?? 'openid'),
          state: authState,
          code_challenge: codeChallenge,
          code_challenge_method: this.pkceManager.CODE_METHOD,
          nonce: await this.stateVerifier.createNonce(),
          response_type: 'code',
          response_mode: 'post_message',
          sessionToken: sessionToken,
        });
        const iframeMessagePromise = addPostMessageListener({
          issuer: this.clientConfig.issuer,
          timeout: 1200000,
          state: authState,
        });
        const removeIframe = createIframe(url);
        try {
          const data = await iframeMessagePromise;
          return await exchangeCodeForTokens({
            code: data.code,
            clientConfig: this.clientConfig,
            stateVerifier: this.stateVerifier,
            router: this.router,
            pkce: this.pkceManager,
            tokenManager: this.tokenManager,
          });
        } finally {
          removeIframe();
        }
      } catch (e) {
        this.logger.error('Failed to acquire tokens', e);
        throw new AuthSdkError('Failed to acquire tokens', e);
      }
    });
  }
}
